import type { GraphQLTaggedNode } from 'relay-runtime';
import { graphql } from 'relay-runtime';

import { keys as sessionStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import type { UseQueryInteropSupportedResult } from '@confluence/apollo-relay-interop/entry-points/QueryInterop.ts';
import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';

import { FollowingFeedWithSortByStorageSnippetsQuery } from './FollowingFeedWithSortByStorageSnippets.graphql.ts';
import type {
	FollowingFeedWithSortByStorageSnippetsQuery$data,
	FollowingFeedWithSortByStorageSnippetsQuery$variables,
} from './__generated__/FollowingFeedWithSortByStorageSnippetsQuery.graphql.ts';
import type {
	useFollowingFeedWithSortByStorageFeedContentQuery$data,
	useFollowingFeedWithSortByStorageFeedContentQuery$variables,
} from './__generated__/useFollowingFeedWithSortByStorageFeedContentQuery.graphql.ts';
// eslint-disable-next-line import/no-cycle
import { FollowingFeedSortByValue } from './Feed.tsx';
import { useFeedDropdownStorage } from './useFeedDropdownStorage.ts';

/*
 * getFeedUserConfig
 *   queried to provide better stats at a small performance cost (~10ms)
 *   if performance optimization becomes more important than experimentation, it can be removed.
 *
 * content excerpt = 350
 *   when excerpt is the full length, it will overflow the 2 line max (with current size and font styles) and get ellipsis via CSS.
 *   if it doesnt overflow, it is the full content and doesnt need ellipsis.
 */
export const useFollowingFeedWithSortByStorageFeedContentQuery: GraphQLTaggedNode = graphql`
	query useFollowingFeedWithSortByStorageFeedContentQuery(
		$first: Int
		$after: String
		$sortBy: String = "relevant"
	) {
		feed(first: $first, after: $after, sortBy: $sortBy) {
			nodes {
				recentActionsCount
				summaryLineUpdate {
					datetime
					type
					groupSize
					... on FeedEventCreate {
						user {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
							... on UnknownUser {
								accountId
							}
						}
					}
					... on FeedEventEdit {
						version
						user {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
							... on UnknownUser {
								accountId
							}
						}
					}
					... on FeedEventEditLive {
						version
						user {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
							... on UnknownUser {
								accountId
							}
						}
					}
					... on FeedEventPublishLive {
						version
						user {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
							... on UnknownUser {
								accountId
							}
						}
					}
					... on FeedEventComment {
						user {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
							... on UnknownUser {
								accountId
							}
						}
						comment {
							id
							links {
								webui
							}
							location {
								type
								... on InlineComment {
									inlineText
								}
							}
							body(representation: ATLAS_DOC_FORMAT) {
								value
							}
						}
					}
				}
				source
				content {
					title
					id
					type
					subType
					emoji: properties(key: "emoji-title-published") {
						nodes {
							id
							value
						}
					}
					contentProperties {
						latest {
							generatedBy
						}
					}
					excerpt(length: 350)
					links {
						webui
					}
					history {
						createdBy {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
							... on UnknownUser {
								accountId
							}
						}
						ownedBy {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
						}
						createdDate
					}
					space {
						alias
						id
						key
						name
					}
					metadata {
						lastModifiedDate
						comments {
							commentsCount
						}
						currentuser {
							favourited {
								isFavourite
							}
						}
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
		getFeedUserConfig {
			spaces {
				id
			}
			users {
				displayName
				profilePicture {
					path(type: ABSOLUTE)
				}
				... on KnownUser {
					accountId
				}
				... on UnknownUser {
					accountId
				}
			}
			servingRecommendations
		}
	}
`;

export const useFollowingFeedWithSortByStorage = (): {
	followingFeedSortBy: FollowingFeedSortByValue;
	setFollowingFeedSortBy: (sorting: FollowingFeedSortByValue) => void;
	followingFeedQuery: UseQueryInteropSupportedResult<
		useFollowingFeedWithSortByStorageFeedContentQuery$data,
		useFollowingFeedWithSortByStorageFeedContentQuery$variables
	>;
} => {
	const { value, setValue, query } = useFeedDropdownStorage<
		useFollowingFeedWithSortByStorageFeedContentQuery$data,
		useFollowingFeedWithSortByStorageFeedContentQuery$variables,
		FollowingFeedSortByValue
	>(
		{
			defaultValue: FollowingFeedSortByValue.Relevant,
			sessionStorageKey: sessionStorageKeys.HOME_FOLLOWING_FEED_SORTING,
			skipQuery: UNSAFE_expValEqualsNoExposure(
				'confluence_ai_snippets_in_following_popular_feeds',
				'isEnabled',
				true,
			),
		},
		{
			query: useFollowingFeedWithSortByStorageFeedContentQuery,
			variables: (
				sorting: FollowingFeedSortByValue,
			): useFollowingFeedWithSortByStorageFeedContentQuery$variables => ({
				sortBy: sorting,
			}),
		},
	);

	const {
		value: valueWithSnippets,
		setValue: setValueWithSnippets,
		query: queryWithSnippets,
	} = useFeedDropdownStorage<
		FollowingFeedWithSortByStorageSnippetsQuery$data,
		FollowingFeedWithSortByStorageSnippetsQuery$variables,
		FollowingFeedSortByValue
	>(
		{
			defaultValue: FollowingFeedSortByValue.Relevant,
			sessionStorageKey: sessionStorageKeys.HOME_FOLLOWING_FEED_SORTING,
			skipQuery: !UNSAFE_expValEqualsNoExposure(
				'confluence_ai_snippets_in_following_popular_feeds',
				'isEnabled',
				true,
			),
			interopApolloAggClient: true,
		},
		{
			query: FollowingFeedWithSortByStorageSnippetsQuery,
			variables: (
				sorting: FollowingFeedSortByValue,
			): FollowingFeedWithSortByStorageSnippetsQuery$variables => ({
				sortBy: sorting,
			}),
		},
	);

	return {
		followingFeedSortBy: UNSAFE_expValEqualsNoExposure(
			'confluence_ai_snippets_in_following_popular_feeds',
			'isEnabled',
			true,
		)
			? valueWithSnippets
			: value,
		setFollowingFeedSortBy: UNSAFE_expValEqualsNoExposure(
			'confluence_ai_snippets_in_following_popular_feeds',
			'isEnabled',
			true,
		)
			? setValueWithSnippets
			: setValue,
		followingFeedQuery: (UNSAFE_expValEqualsNoExposure(
			'confluence_ai_snippets_in_following_popular_feeds',
			'isEnabled',
			true,
		)
			? queryWithSnippets
			: query) as UseQueryInteropSupportedResult<
			useFollowingFeedWithSortByStorageFeedContentQuery$data,
			useFollowingFeedWithSortByStorageFeedContentQuery$variables
		>,
	};
};
